import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import APIClient from '../../../lib/api';
import PageHeader from '../../../components/common/PageHeader';
import PaginationComponent from '../../../components/common/PaginationComponent';
import MetricsCard from '../../../components/common/MetricsCard';
import ServiceTable from '../components/ServiceTable';

const PublicServiceListingPage = () => {
  const { token } = useParams();
  
  // Helper function for currency formatting
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0,00 €';
    }
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  // Data states
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientName, setClientName] = useState('');
  
  // Statistics state
  const [statistics, setStatistics] = useState({
    total_services: 0,
    total_credit: 0,
    total_cost_with_vat: 0,
    net_cost: 0,
    total_paid: 0,
    balance: 0
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalServices, setTotalServices] = useState(0);

  // Sorting states
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const normalizedError = typeof error === 'string'
    ? error
    : error && typeof error === 'object'
      ? (error.detail || error.message || '')
      : '';

  // Load services function
  const loadServices = useCallback(async () => {
    if (!token) {
      setError('Λάθος ή έλλειψη token');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        sort_by: sortField,
        sort_order: sortDirection
      };
      
      const response = await APIClient.services.getPublicServices(token, params);
      
      if (response.data) {
        setServices(response.data.data || []);
        setTotalServices(response.data.total || 0);
        
        // Get client name from first service if available
        if (response.data.data && response.data.data.length > 0 && response.data.data[0].client) {
          setClientName(response.data.data[0].client.name || '');
        }
      } else {
        setError('Σφάλμα φόρτωσης υπηρεσιών');
        setServices([]);
        setTotalServices(0);
      }
    } catch (err) {
      console.error('Error loading public services:', err);
      setError(err.response?.data?.detail || 'Σφάλμα φόρτωσης υπηρεσιών');
      setServices([]);
      setTotalServices(0);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, itemsPerPage, sortField, sortDirection]);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    if (!token) return;

    try {
      const response = await APIClient.services.getPublicStatistics(token);
      if (response.data) {
        setStatistics(response.data || {
          total_services: 0,
          total_credit: 0,
          total_cost_with_vat: 0,
          net_cost: 0,
          total_paid: 0,
          balance: 0
        });
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  }, [token]);

  // Load data on mount and when filters change
  useEffect(() => {
    loadServices();
    loadStatistics();
  }, [loadServices, loadStatistics]);

  // Handle actions
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  if (normalizedError && (normalizedError.includes('Invalid') || normalizedError.includes('expired'))) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography variant="h5" color="error">
          Λάθος ή έλλειψη συνδέσμου. Παρακαλώ επικοινωνήστε με τον διαχειριστή.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <PageHeader
        title={clientName ? `Υπηρεσίες - ${clientName}` : 'Οι Υπηρεσίες μου'}
        subtitle="Προβολή υπηρεσιών (Μόνο ανάγνωση)"
        icon="⚙️"
        actions={[]}
      />

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
        <MetricsCard
          title="Σύνολο Υπηρεσιών"
          value={statistics.total_services}
          color="primary"
        />
        <MetricsCard
          title="Συνολικό Κόστος"
          value={formatCurrency(statistics.total_cost_with_vat || statistics.total_credit || 0)}
          subtitle={statistics.net_cost !== undefined ? `Καθαρό κόστος: ${formatCurrency(statistics.net_cost || 0)}` : null}
          color="info"
        />
        <MetricsCard
          title="Εξοφλημένο"
          value={formatCurrency(statistics.total_paid)}
          color="success"
        />
        <MetricsCard
          title="Υπόλοιπο"
          value={formatCurrency(statistics.balance)}
          color="warning"
        />
      </Box>

      {/* Error Display */}
      {normalizedError && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">
            {normalizedError}
          </Typography>
        </Box>
      )}

      {/* Data Table - Read Only (no onEdit, onDelete) */}
      <ServiceTable
        services={services}
        loading={loading}
        onEdit={null}
        onDelete={null}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        multiSelectMode={false}
        selectedServices={[]}
        onServiceSelect={null}
        onSelectAll={null}
      />

      {/* Pagination */}
      <PaginationComponent
        currentPage={currentPage}
        totalPages={Math.ceil(totalServices / itemsPerPage)}
        totalItems={totalServices}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </Box>
  );
};

export default PublicServiceListingPage;

